import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { parseISO, subWeeks } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import { Avatar, Box, Chip, Typography } from '@mui/material';
import MUIDataTable from 'mui-datatables';
import DashboardBlock from 'components/shared/blocks/DashboardBlock';
import LoadingBlock from 'components/shared/blocks/LoadingBlock';
import { dateFormat, timeFormat } from 'common/constants/date';
import { convertUtcToLocalTime } from 'common/utils/date';
import { defaultTableOptions, tableLocalization } from 'common/table/utils';

const STATUS_COLOR = { SCHEDULED: 'primary', FINISHED: 'success', CANCELLED: 'error' };
const STATUS_LABEL = { SCHEDULED: 'Programada', FINISHED: 'Finalizada', CANCELLED: 'Cancelada' };

function getColumns(tableData, t) {
  return [
    { label: t('date', { ns: 'labels' }), name: 'date' },
    { label: t('time', { ns: 'labels' }), name: 'time' },
    {
      label: t('trainer', { count: 1, ns: 'labels' }),
      name: 'trainer',
      options: {
        customBodyRenderLite: (dataIndex) => {
          const { trainer } = tableData[dataIndex];
          if (!trainer) return <Typography variant="body2" color="text.secondary">—</Typography>;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar src={trainer.photo} alt={trainer.full_name} sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                {trainer.full_name?.[0]}
              </Avatar>
              <Typography variant="body2">{trainer.full_name}</Typography>
            </Box>
          );
        }
      }
    },
    { label: t('category', { ns: 'labels' }), name: 'category' },
    {
      label: t('status', { ns: 'labels' }),
      name: 'status',
      options: {
        customBodyRenderLite: (dataIndex) => {
          const { status } = tableData[dataIndex];
          return (
            <Chip label={STATUS_LABEL[status] ?? status} color={STATUS_COLOR[status] ?? 'default'} size="small" />
          );
        }
      }
    }
  ];
}

function CompletedGroupSessionsList({ emptyMessage, itemsPerPage = 5 }) {
  const { t, i18n } = useTranslation(['labels', 'routes', 'sessions', 'tables']);
  const dateLocale = i18n.language === 'es' ? es : enUS;

  const sessions = useSelector((state) => state.groupSession.data.myGroupSessions);
  const sessionStatus = useSelector((state) => state.groupSession.status.general);
  const currentUserId = useSelector((state) => state.auth.user.id);

  const twoWeeksAgo = subWeeks(new Date(), 2);

  const finishedSessions = sessions
    .filter((s) => {
      const myAttendance = s.attendances?.find((a) => String(a.user?.id) === String(currentUserId));
      const displayStatus = myAttendance?.status ?? s.status;
      if (displayStatus !== 'FINISHED' && displayStatus !== 'PRESENT') return false;
      return parseISO(s.start_date) >= twoWeeksAgo;
    })
    .sort((a, b) => parseISO(b.start_date) - parseISO(a.start_date))
    .slice(0, itemsPerPage);

  const tableData = finishedSessions.map((s) => {
    const myAttendance = s.attendances?.find((a) => String(a.user?.id) === String(currentUserId));
    const displayStatus = myAttendance?.status ?? s.status;
    return {
      id: s.id,
      date: convertUtcToLocalTime(s.start_date, dateFormat, dateLocale),
      time: `${convertUtcToLocalTime(s.start_date, timeFormat)} – ${convertUtcToLocalTime(s.end_date, timeFormat)}`,
      trainer: s.trainer,
      category: s.session_type_id?.name ?? '—',
      status: displayStatus,
    };
  });

  const columns = getColumns(tableData, t);

  const options = {
    ...defaultTableOptions,
    jumpToPage: false,
    rowsPerPage: itemsPerPage,
    selectableRows: 'none',
    serverSide: false,
    textLabels: tableLocalization(t('session.list', { ns: 'routes' }).toLowerCase(), t)
  };

  return (
    <DashboardBlock
      title={t('sessionsCompleted', { ns: 'sessions' })}
      titleNote={t('lastNWeeks', { num: 2, ns: 'common' })}>
      <LoadingBlock
        emptyBlockProps={{ message: emptyMessage }}
        isValid={finishedSessions.length > 0}
        skeletonProps={{ rows: itemsPerPage }}
        status={sessionStatus}
        type="table">
        <MUIDataTable
          className="finishedSessions"
          columns={columns}
          data={tableData}
          options={options}
        />
      </LoadingBlock>
    </DashboardBlock>
  );
}

CompletedGroupSessionsList.propTypes = {
  emptyMessage: PropTypes.string.isRequired,
  itemsPerPage: PropTypes.number
};

export default CompletedGroupSessionsList;
